import React, { useState } from 'react';
import { DollarSign, Printer, Send, Download, Calendar } from 'lucide-react';
import { sendInvoiceEmail } from '../utils/emailService';
import {
  generateRecurringInvoices,
  generateSingleInvoice,
  exportInvoicesToCSV,
  downloadCSV,
  type Invoice,
} from '../utils/invoiceUtils';
import InvoicePreview from '../components/InvoicePreview';

const InvoicePage = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [invoiceType, setInvoiceType] = useState<'recurring' | 'single'>('single');
  const [generatedInvoices, setGeneratedInvoices] = useState<Invoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const logo = localStorage.getItem('companyLogo');

  // Mock student data - in a real app, this would come from your backend
  const studentData = [
    {
      studentId: '12345',
      studentName: 'John Doe',
      unitNo: '304A',
    },
    {
      studentId: '12346',
      studentName: 'Jane Smith',
      unitNo: '305B',
    },
  ];

  const handleGenerateInvoices = () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) {
      alert('End date must be after start date');
      return;
    }

    let invoices: Invoice[];
    if (invoiceType === 'recurring') {
      invoices = generateRecurringInvoices(studentData, start, end);
    } else {
      invoices = studentData.map(student => generateSingleInvoice(student, start, end));
    }
    
    setGeneratedInvoices(invoices);
  };

  const handleExportCSV = () => {
    if (generatedInvoices.length === 0) {
      alert('No invoices to export');
      return;
    }

    const csvContent = exportInvoicesToCSV(generatedInvoices);
    const filename = `invoices_${startDate}_to_${endDate}.csv`;
    downloadCSV(csvContent, filename);
  };

  const handlePreviewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsPreviewOpen(true);
  };

  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;

    try {
      await sendInvoiceEmail(
        'student@example.com', // Replace with actual student email
        selectedInvoice.studentName,
        selectedInvoice.invoiceNumber,
        selectedInvoice.totalAmount,
        selectedInvoice.dueDate
      );
      
      setIsPreviewOpen(false);
      alert('Invoice sent successfully');
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Error sending invoice');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Invoice Management</h1>
        <div className="space-x-4">
          <button 
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            onClick={handleExportCSV}
            disabled={generatedInvoices.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export to CSV
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Generate Invoices</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Invoice Type
          </label>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="single"
                checked={invoiceType === 'single'}
                onChange={(e) => setInvoiceType(e.target.value as 'single' | 'recurring')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700 dark:text-gray-300">Single Invoice</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="recurring"
                checked={invoiceType === 'recurring'}
                onChange={(e) => setInvoiceType(e.target.value as 'single' | 'recurring')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700 dark:text-gray-300">Recurring Monthly</span>
            </label>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
        </div>
        <button
          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          onClick={handleGenerateInvoices}
        >
          <Calendar className="h-4 w-4 mr-2" />
          Generate Invoices
        </button>
      </div>

      {generatedInvoices.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Generated Invoices</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b dark:border-gray-700">
                  <th className="text-left py-3 text-gray-700 dark:text-gray-300">Invoice #</th>
                  <th className="text-left py-3 text-gray-700 dark:text-gray-300">Student</th>
                  <th className="text-left py-3 text-gray-700 dark:text-gray-300">Unit No</th>
                  <th className="text-left py-3 text-gray-700 dark:text-gray-300">Period</th>
                  <th className="text-right py-3 text-gray-700 dark:text-gray-300">Amount</th>
                  <th className="text-center py-3 text-gray-700 dark:text-gray-300">Status</th>
                  <th className="text-right py-3 text-gray-700 dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {generatedInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b dark:border-gray-700">
                    <td className="py-3 text-gray-900 dark:text-gray-100">{invoice.invoiceNumber}</td>
                    <td className="py-3 text-gray-900 dark:text-gray-100">{invoice.studentName}</td>
                    <td className="py-3 text-gray-900 dark:text-gray-100">{invoice.unitNo || 'N/A'}</td>
                    <td className="py-3 text-gray-900 dark:text-gray-100">
                      {new Date(invoice.startDate).toLocaleDateString()} - {new Date(invoice.endDate).toLocaleDateString()}
                    </td>
                    <td className="text-right py-3 text-gray-900 dark:text-gray-100">{formatCurrency(invoice.totalAmount)}</td>
                    <td className="text-center py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : invoice.status === 'overdue'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="text-right py-3">
                      <button
                        className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        onClick={() => handlePreviewInvoice(invoice)}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Preview & Send
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedInvoice && (
        <InvoicePreview
          invoice={selectedInvoice}
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setSelectedInvoice(null);
          }}
          onSend={handleSendInvoice}
        />
      )}
    </div>
  );
};

export default InvoicePage;